import type { RenderTooltipContentData } from '@shopify/polaris-viz';
import type { IRMetricKey } from '../../../../common/type';

import { BlockStack, Box, InlineStack, Text } from '@shopify/polaris';
import { formatPercent } from '~/presentation/reports/viz/utils';
import { isReverseReportMetric } from '../../../../common/type';
import { IncreasePercentage } from '~/modules/analytics';
import { WrapperTooltip } from './WrapperTooltip';

interface SingleSeriesTooltipProps {
  data: RenderTooltipContentData;
  activeSeriesKey?: number;
  title: string;
  formatValue: (value: number) => string;
  formatKeyByInterval?: (value: string | number | null, isExpandDetail?: boolean) => string;
  metricKey?: IRMetricKey;
}

export const SingleSeriesTooltip = ({
  data,
  title,
  activeSeriesKey,
  formatKeyByInterval,
  formatValue,
  metricKey,
}: SingleSeriesTooltipProps) => {
  if (activeSeriesKey === undefined) return null;
  const { activeIndex } = data;

  const currentSeries = data.dataSeries?.[activeSeriesKey];
  if (!currentSeries) return null;

  const currentData = currentSeries.data?.[activeIndex];
  if (!currentData) return null;

  const comparisonIndex = currentSeries.metadata?.comparisonIndex;
  if (comparisonIndex === undefined) return null;

  const comparisonSeries = data.dataSeries?.[comparisonIndex];
  if (!comparisonSeries) return null;

  const comparisonData = comparisonSeries.data?.[activeIndex];
  const increase = formatPercent(currentData);

  const currentName = formatKeyByInterval?.(currentData?.key, true) || currentData?.key;
  const comparisonName = formatKeyByInterval?.(comparisonData?.key, true) || comparisonData?.key;

  return (
    <WrapperTooltip title={title}>
      <BlockStack gap={'200'}>
        <Text as="p" variant="bodySm" tone="subdued">
          {currentSeries.name}
        </Text>
        <SingleSeriesInfo
          name={`${currentName}`}
          value={currentData.value}
          increase={increase}
          color={`${currentSeries.color}`}
          formatValue={formatValue}
          metricKey={metricKey}
        />
        <SingleSeriesInfo
          name={`${comparisonName}`}
          value={comparisonData.value}
          color={`${currentSeries.color}`}
          isCompare={true}
          formatValue={formatValue}
        />
      </BlockStack>
    </WrapperTooltip>
  );
};

interface SingleSeriesInfoProps {
  name: string;
  value: number | null;
  increase?: number | string;
  color: string;
  isCompare?: boolean;
  formatValue: (value: number) => string;
  metricKey?: IRMetricKey;
}

const SingleSeriesInfo = ({
  name,
  value,
  increase,
  color,
  isCompare,
  formatValue,
  metricKey,
}: SingleSeriesInfoProps) => {
  return (
    <BlockStack gap={'200'}>
      <InlineStack gap={'100'} blockAlign="center">
        <div
          className="w-[8px] h-[8px] rounded-full"
          style={{
            backgroundColor: color,
            opacity: isCompare ? 0.6 : 1,
          }}
        />
        <Text as="p" variant="bodyXs" tone="subdued">
          {name}
        </Text>
      </InlineStack>
      <Box paddingInlineStart={'300'}>
        <InlineStack blockAlign="center" gap="100">
          <Text as="span" variant="bodySm" fontWeight="semibold">
            {formatValue(value || 0)}
          </Text>
          {increase !== undefined && (
            <IncreasePercentage increase={increase} isReverse={isReverseReportMetric(metricKey)} />
          )}
        </InlineStack>
      </Box>
    </BlockStack>
  );
};
